import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { type AuthLink, readAuthLink, type AuthLinkType } from '../lib/authLink';

/**
 * The session of the page that sets a password (`/reset-password`), whichever way the person arrived (audit of
 * L6h + L6f, A1):
 * - a link the functions built (an invitation, a recovery link handed over by an administrator): `?token_hash=…&type=…`,
 *   verified here with `verifyOtp`, which works in the cockpit's PKCE flow; the token leaves the address bar first (it is
 *   single-use, and the history keeps addresses);
 * - « Mot de passe oublié » (the person's own browser, PKCE): the client exchanges the code of the URL itself, and the
 *   session arrives as `PASSWORD_RECOVERY`; `invalid` after 5 seconds without one.
 */
export type AuthLinkStatus = 'checking' | 'ready' | 'invalid';

export interface AuthLinkSession {
  readonly status: AuthLinkStatus;
  /** An invitation, or a password to set again (recovery and « Mot de passe oublié »). */
  readonly kind: AuthLinkType;
}

const WAIT_MS = 5000;

// One verification per token: the token is single-use, and an effect may run twice (StrictMode).
const verifications = new Map<string, Promise<boolean>>();

function verifyOnce(link: AuthLink): Promise<boolean> {
  let pending = verifications.get(link.tokenHash);
  if (!pending) {
    pending = supabase.auth
      .verifyOtp({ token_hash: link.tokenHash, type: link.type })
      .then(({ data, error }) => !error && data.session !== null);
    verifications.set(link.tokenHash, pending);
  }
  return pending;
}

export function useAuthLinkSession(): AuthLinkSession {
  // Read once, before the effect takes the token out of the address bar.
  const [link] = useState(() => readAuthLink(window.location.search));
  const [status, setStatus] = useState<AuthLinkStatus>('checking');

  useEffect(() => {
    let active = true;
    const settle = (next: AuthLinkStatus) => {
      if (active) setStatus((current) => (current === 'checking' ? next : current));
    };
    if (link) {
      window.history.replaceState(null, '', window.location.pathname);
      void verifyOnce(link).then((ok) => settle(ok ? 'ready' : 'invalid'));
      return () => {
        active = false;
      };
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && event === 'PASSWORD_RECOVERY') settle('ready');
    });
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) settle('ready');
    });
    const timeout = window.setTimeout(() => settle('invalid'), WAIT_MS);
    return () => {
      active = false;
      subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, [link]);

  return { status, kind: link?.type ?? 'recovery' };
}
